import React from 'react';
import TextField, { TextFieldProps } from '@mui/material/TextField';

type FieldError = string | string[] | undefined;

export interface FormTextFieldProps
  extends Omit<TextFieldProps, 'value' | 'error' | 'helperText' | 'onChange'> {
  name: string;
  value: string;
  fieldError?: FieldError;
  onFieldChange: (name: string, value: string) => void;
  onClearError?: (name: string) => void;
}

/**
 * A wrapper around MUI TextField that simplifies form field handling.
 * Automatically handles:
 * - Error display (supports both string and string[] errors)
 * - onChange with automatic error clearing
 * - Common styling defaults (color='info', fullWidth)
 */
export default function FormTextField({
  name,
  value,
  fieldError,
  onFieldChange,
  onClearError,
  ...props
}: FormTextFieldProps) {
  const hasError = !!fieldError;
  // Only show string errors in helperText; array errors should use PasswordErrorList
  const helperText = typeof fieldError === 'string' ? fieldError : undefined;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFieldChange(name, e.target.value);
    if (hasError && onClearError) {
      onClearError(name);
    }
  };

  return (
    <TextField
      id={name}
      name={name}
      color='info'
      fullWidth
      value={value}
      error={hasError}
      helperText={helperText}
      onChange={handleChange}
      {...props}
    />
  );
}
